import React, {useEffect} from "react";
import {Button, Form, Input, Select} from "antd";
import type {FormInstance} from "antd";
import {useTranslation} from "react-i18next";
import styles from "./CoachForm.module.scss";
import {CoachProfessionalInfo} from "../../types/coachApplication";

const {Option} = Select;
const {TextArea} = Input;

const capitalizeFirstLetter = (value?: string): string => {
  if (!value) return value ?? "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const LANGUAGES_PATTERN = /^[A-Za-zçÇëËäÄöÖüÜ\s]+(,\s*[A-Za-zçÇëËäÄöÖüÜ\s]+)*$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_VALIDATION_PATTERN = /^\+?(?!0+$)[0-9]{8,15}$/;

interface CoachStepThreeProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  initialValues?: CoachProfessionalInfo;
  persistToSession?: boolean;
  onSave?: (values: CoachProfessionalInfo) => void;
  embedded?: boolean;
  reviewLayout?: boolean;
  form?: FormInstance<CoachProfessionalInfo>;
}

const CoachStepThree: React.FC<CoachStepThreeProps> = ({
       setCurrentStep,
       initialValues,
       persistToSession = true,
       onSave,
       embedded = false,
       reviewLayout = false,
       form: externalForm,
   }) => {
  const {t, i18n} = useTranslation();
  const [internalForm] = Form.useForm<CoachProfessionalInfo>();
  const form = externalForm || internalForm;

  useEffect(() => {
    if (!initialValues) return;
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);
  
  useEffect(() => {
    const fields = form.getFieldsError();
    const fieldsWithErrors = fields
      .filter(({ errors }) => errors.length > 0)
      .map(({ name }) => name);

    if (fieldsWithErrors.length > 0) {
      form.validateFields(fieldsWithErrors);
    }
  }, [i18n.language, form]);

  // Signature disabled for the moment — kept for future re-enable.
  // const canvasRef = useRef<HTMLCanvasElement>(null);
  // const [isDrawing, setIsDrawing] = useState(false);
  // const [hasSignature, setHasSignature] = useState(false);
  // const [signatureError, setSignatureError] = useState(false);
  // const lastPos = useRef<{ x: number; y: number } | null>(null);

  // const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
  //   const rect = canvas.getBoundingClientRect();
  //   if ("touches" in e) {
  //     return {
  //       x: e.touches[0].clientX - rect.left,
  //       y: e.touches[0].clientY - rect.top,
  //     };
  //   }
  //   return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  // };

  // const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
  //   const canvas = canvasRef.current;
  //   if (!canvas) return;
  //   e.preventDefault();
  //   setIsDrawing(true);
  //   lastPos.current = getPos(e, canvas);
  // };

  // const draw = (e: React.MouseEvent | React.TouchEvent) => {
  //   if (!isDrawing) return;
  //   const canvas = canvasRef.current;
  //   const ctx = canvas?.getContext("2d");
  //   if (!canvas || !ctx || !lastPos.current) return;
  //   e.preventDefault();

  //   const currentPos = getPos(e, canvas);
  //   ctx.beginPath();
  //   ctx.moveTo(lastPos.current.x, lastPos.current.y);
  //   ctx.lineTo(currentPos.x, currentPos.y);
  //   ctx.strokeStyle = "#000";
  //   ctx.lineWidth = 2;
  //   ctx.lineCap = "round";
  //   ctx.stroke();
  //   lastPos.current = currentPos;
  //   setHasSignature(true);
  //   setSignatureError(false);
  // };

  // const stopDrawing = () => {
  //   setIsDrawing(false);
  //   lastPos.current = null;
  // };

  // const clearSignature = () => {
  //   const canvas = canvasRef.current;
  //   const ctx = canvas?.getContext("2d");
  //   if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  //   setHasSignature(false);
  // };

  const onFinish = (values: CoachProfessionalInfo) => {
    // Signature disabled for the moment — skipping the hasSignature check
    // and not persisting a signature data URL.
    if (persistToSession) {
      sessionStorage.setItem("coachStepThree", JSON.stringify({...values}));
    }
    onSave?.(values);
    if (!embedded) {
      setCurrentStep(3);
    }
  };

  const handleValuesChange = async (
      _: Partial<CoachProfessionalInfo>,
      allValues: CoachProfessionalInfo
  ) => {
    if (!embedded) return;
    try {
      await form.validateFields();
      onSave?.(allValues);
    } catch {
    }
  };

  return (
      <div
          className={`${styles.CoachFormContainer} ${embedded || reviewLayout ? styles.CoachFormContainerReview : ""}`}
      >
        <div className={styles.CoachFormTitle}>
          <h1>{t("app.coaches.stepThree.title")}</h1>
          <p>{t("app.coaches.stepThree.subtitle")}</p>
        </div>

        <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={handleValuesChange}
        >
          <div className={styles.CoachForm}>
            {/* Row 1: Clothing size + Languages */}
            <div className={styles.CoachFormAlignment}>
              <div className={styles.CoachFormField}>
                <p>{t("app.coaches.shoeSize")}</p>
                <Form.Item
                    name="clothingSize"
                    rules={[
                      {required: true, message: t("app.validations.required")},
                    ]}
                >
                  <Select placeholder={t("app.coaches.selectSize")}>
                    {["S", "M", "L", "XL", "XXL"].map((size) => (
                        <Option key={size} value={size}>
                          {size}
                        </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className={styles.CoachFormField}>
                <p>{t("app.coaches.languages")}</p>
                <Form.Item
                    name="languageSkills"
                     normalize={capitalizeFirstLetter}
                    rules={[
                      {required: true, message: t("app.validations.required")},
                      {
                        pattern: LANGUAGES_PATTERN,
                        message: t("app.coaches.validation.languages"),
                      },
                    ]}
                >
                  <Input placeholder={t("app.coaches.languagesPlaceholder")}/>
                </Form.Item>
              </div>
            </div>

            <div className={styles.CoachFormFullWidth}>
              <p>{t("app.coaches.footballCareer")}</p>
              <Form.Item
                  name="careerAsPlayer"
                   normalize={capitalizeFirstLetter}
                  rules={[
                    {required: true, message: t("app.validations.required")},
                  ]}
              >
                <TextArea rows={4}/>
              </Form.Item>
            </div>

            <div className={styles.CoachFormFullWidth}>
              <p>{t("app.coaches.coachingCareer")}</p>
              <Form.Item
                  name="careerAsCoach"
                   normalize={capitalizeFirstLetter}
                  rules={[
                    {required: true, message: t("app.validations.required")},
                  ]}
              >
                <TextArea rows={4}/>
              </Form.Item>
            </div>

            <div className={styles.CoachFormFullWidth}>
              <p>{t("app.coaches.otherExperience")}</p>
              <Form.Item
                  name="otherExperience"
                   normalize={capitalizeFirstLetter}
                  rules={[
                    {required: true, message: t("app.validations.required")},
                  ]}
              >
                <TextArea rows={4}/>
              </Form.Item>
            </div>

            <div className={styles.CoachFormAlignment}>
              <div className={styles.CoachFormField}>
                <p>{t("app.coaches.phone")}</p>
                <Form.Item
                    name="phoneNumber"
                    rules={[
                      {required: true, message: t("app.validations.required")},
                      {
                        pattern: PHONE_VALIDATION_PATTERN,
                        message: t("app.coaches.validation.phone"),
                      },
                    ]}
                >
                  <Input placeholder="+38344000000"/>
                </Form.Item>
              </div>

              <div className={styles.CoachFormField}>
                <p>{t("app.coaches.email")}</p>
                <Form.Item
                    name="email"
                    rules={[
                      {required: true, message: t("app.validations.required")},
                      {
                        pattern: EMAIL_PATTERN,
                        message: t("app.volunteers.validation.emailFormat"),
                      },
                    ]}
                >
                  <Input type="email"/>
                </Form.Item>
              </div>
            </div>

            {/* Signature disabled for the moment — kept for future re-enable.
            <div className={styles.CoachFormFullWidth}>
              <p>{t("app.coaches.signature", "Nënshkrimi")}</p>
              <div className={styles.SignatureWrapper}>
                <canvas
                    ref={canvasRef}
                    width={800}
                    height={150}
                    className={styles.SignatureCanvas}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
                {signatureError && (
                    <div className={styles.SignatureError}>
                      Nënshkrimi është i detyrueshëm
                    </div>
                )}
                <Button
                    type="default"
                    size="small"
                    onClick={clearSignature}
                    className={styles.ClearSignatureBtn}
                >
                  Fshij nënshkrimin
                </Button>
              </div>
            </div>
            */}
          </div>

          {!embedded && (
              <div className={styles.ButtonAlignment}>
                <Button
                    onClick={() => setCurrentStep(1)}
                    className={`${styles.ButtonStyle} ${styles.ButtonBack}`}
                >
                  {t("app.coaches.back")}
                </Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    className={styles.ButtonStyle}
                >
                  {t("app.coaches.continue")}
                </Button>
              </div>
          )}
        </Form>
      </div>
  );
};

export default CoachStepThree;